import { defineStore } from "pinia";
import { colorMapper } from '~/constants/common.js'
import filter from 'lodash/filter'
import { CERTIFICATION } from '~/constants/common.js'

export const useSearchStore = defineStore("search", () => {
  const { locale } = useI18n()
  const isColorSearch = ref(false)
  const showSearchSideBar = ref(false)
  const joinedCountries = ref([])
  const yarnTypes = ref([])
  const characteristics = ref([])
  const functionals = ref([])
  const yarnCounts = ref([])
  const gauges = ref([])
  const yarnPriceType = ref([])
  const colors = ref([])
  const isMyYarnPage = ref(false)
  const sortBy = ref(null)
  const sortByOptions = ref([])
  const sortByYarnPage = ref(null)
  const yarnMaterial = ref([])
  const gaugeEnds = ref([])
  const certifications = ref([])
  const seasons = ref([])
  const labels = ref([])
  const stocks = ref([])
  const allMaterials = ref([])

  const getJoinedCountry = async (placeholder) => {
    const result = await usePost('/company/q/s/country', { locale: locale.value })
    const rows = result.data.data
    joinedCountries.value = rows.map(({ countryName, countryCode, ...rest }) => {
      return {
        text: countryName,
        value: countryCode,
        ...rest
      }
    })
  }

  const getTypesFromServer = async () => {
    const result = await usePost('/code/q/a/', { mcdId: 108, locale: locale.value })
    const row = result.data.data
    return yarnTypes.value = row.map((yarnType) => {
      return {
        ...yarnType,
        text: yarnType.codeName,
        value: yarnType.codeId
      }
    })
  }

  const getCharacteristicsFromServer = async () => {
    const result = await usePost('/code/q/a', { mcdId: 101, locale: locale.value })
    const row = result.data.data
    return characteristics.value = row.map((data) => {
      return {
        ...data,
        text: data.codeName,
        value: data.codeId
      }
    })
  }

  const getFunctionalsFromServer = async () => {
    const result = await usePost('/code/q/a', { mcdId: 110, locale: locale.value })
    const rows = result.data.data
    functionals.value = rows.map((data) => {
      return {
        ...data,
        text: data.codeName,
        value: data.codeId
      }
    })
  }

  const setCount = async () => {
    const result = await usePost('/yarn/q/yc')
    const rows = result.data.data
    yarnCounts.value = rows.map(({ yarnCount }) => {
      return yarnCount
    })
  }

  const getGaugeFromServer = async () => {
    const result = await usePost('/code/q/a/', { mcdId: 106, locale: locale.value })
    const rows = result.data.data
    return gauges.value = rows.map((data) => {
      return {
        ...data,
        text: data.codeName,
        value: data.codeId
      }
    })
  }

  const gaugeOptions = (defaultText) => {
    if (gauges.value.length) {
      return filter(
        [
          defaultText ? { value: null, text: defaultText } : null,
          ...gauges.value
        ],
        item => {
          return item !== null
        }
      )
      
    } else {
      return []
    }
  }

  const setPriceTypes = async () => {
    const result = await usePost('/code/q/a/', { mcdId: 107, locale: locale.value })
    const rows = result.data.data
    yarnPriceType.value = rows.map(priceZone => {
      return {
        ...priceZone,
        text: priceZone.codeName,
        value: priceZone.codeId
      }
    })
  }

  const priceTypeOptions = (defaultText) => {
    if (yarnPriceType.value.length) {
      if (defaultText) {
        return [{ value: null, text: defaultText }, ...yarnPriceType.value]
      } else {
        return yarnPriceType.value
      }
    }
  }

  const getColorsFromServer = async () => {
    const result = await usePost('/code/q/a/', { mcdId: 102, locale: locale.value })
    const rows = result.data.data
    colors.value = colorMapper.map(({ codeId, ...rest }) => {
      return {
        ...rest,
        ...rows.find(colorInfo => codeId === colorInfo.codeId)
      }
    })
  }

  const getSortByOptionsFromServer = async () => {
    const data = await usePost('/code/q/a/', { mcdId: 28, locale: locale.value })
    const rows = data.data.data
    const result = rows.map((item) => {
      return {
        ...item,
        text: item.codeName,
        value: item.codeId
      }
    })
    sortByOptions.value = result
    sortBy.value = parseInt(localStorage.getItem('sortBy')) || result[0].value
    sortByYarnPage.value = parseInt(localStorage.getItem('sortByYarnPage')) || rows[0].value
  }

  const getKeywordList = async (words) => {
    const result = await usePost('/words/q', { search: words })
    return result.data.data.map(({ value }) => {
      return value
    })
  }

  const setMaterials = async (relationShip) => {
    const params = {
      mcdId: 105,
      locale: locale.value,
      relationShip
    }
    const result = await usePost('/code/q/a', params)
    yarnMaterial.value = result.data.data.map(material => {
      return {
        ...material,
        text: material.codeName,
        value: material.codeId
      }
    })
  }
  
  const getCertificationFromServer = async () => {
    const result = await usePost('/code/q/a/', { mcdId: 103, locale: locale.value })
    const row = result.data.data
    certifications.value = row.map((data) => {
      return {
        ...data,
        text: data.codeName,
        value: data.codeId
      }
    })
  }
  const certificationForOption = async (list) => {
    return list && list.length ?
      list.filter(({ value }) => {
        return value !== CERTIFICATION.WOOLMARK
      }) : []
  }

  const getSeasonFromServer = async () => {
    if (!seasons.value.length) {
      const result = await usePost('/code/q/a/', { mcdId: 20, locale: locale.value })
      const row = result.data.data
      seasons.value = row.map((data) => {
        return {
          ...data,
          text: data.codeName,
          value: data.codeId
        }
      })
    }
  }

  const getGaugeEndsFromServer = async () => {
    if(!(gaugeEnds.value && gaugeEnds.length)) {
      gaugeEnds.value = [
        {
          text: '',
          value: null
        }
      ]
      const data = await usePost('/code/q/a/', { mcdId: 112, locale: locale.value })
      const rows = data.data.data
      gaugeEnds.value = rows.map((item) => {
        return {
          ...item,
          text: item.codeName,
          value: item.seq
        }
      })
    }
  }

  const getLabelFromServer = async () => {
    if (!(labels && labels.length)) {
      labels.value = [{ text: 'waiting text', value: null }]
      const data = await usePost('/code/q/a/', { mcdId: 111, locale: locale.value })
      const rows = data.data.data
      labels.value = rows.map((item) => {
        return {
          ...item,
          text: item.codeName,
          value: item.codeId
        }
      })
    }
  }

  const getStocksFromServer = async () => {
    const res = await usePost('/code/q/a/', { mcdId: 113, locale: locale.value })
    const rows = res.data.data
    stocks.value = rows.map((item) => {
      return {
        ...item,
        text: item.codeName,
        value: item.codeId
      }
    })
  }

  const getAllMaterial = async (locale) => {
    const result = await usePost('/code/q/a', { mcdId: 105, locale })
    const rows = result.data.data
    allMaterials.value = rows.map((material) => {
      return {
        ...material,
        text: material.codeName,
        value: material.codeId
      }
    })
    return allMaterials.value
  }

  const getMaterialSub = async (codeId) => {
    const result = await usePost('/code/q/r', { codeId, locale: locale.value })
    const rows = result.data.data
    return rows.map((data) => {
      return {
        ...data,
        text: data.codeName,
        value: data.codeId
      }
    })
  }

  return {
    isColorSearch,
    yarnTypes,
    characteristics,
    functionals,
    yarnCounts,
    showSearchSideBar,
    gauges,
    yarnPriceType,
    colors,
    isMyYarnPage,
    sortByOptions,
    sortByYarnPage,
    seasons,
    labels,
    yarnMaterial,
    joinedCountries,
    gaugeEnds,
    certifications,
    sortBy,
    stocks,
    allMaterials,
    certificationForOption,
    gaugeOptions,
    priceTypeOptions,
    getJoinedCountry,
    getTypesFromServer,
    getCharacteristicsFromServer,
    getFunctionalsFromServer,
    setCount,
    getGaugeFromServer,
    setPriceTypes,
    getColorsFromServer,
    getSortByOptionsFromServer,
    getKeywordList,
    setMaterials,
    getCertificationFromServer,
    getSeasonFromServer,
    getGaugeEndsFromServer,
    getLabelFromServer,
    getStocksFromServer,
    getAllMaterial,
    getMaterialSub
  }
})